import BorderColorIcon from '@mui/icons-material/BorderColor';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion,AccordionSummary,Button,Divider,IconButton,Stack,Tooltip,Typography } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEye,FaEyeSlash } from 'react-icons/fa';
import { trackEventMatomo } from '../../../helpers/matomo';
import { logPlanoType,planoDeAcaoType } from '../../../types/plano-de-acao/types';
import ImagesCmp from '../../ImagesCmp';
import ModalEditPlan from './EditaPlano';
import ModalLogs from './Logs';

type props = {
  plano: planoDeAcaoType
};

export default function DetalhePlano({ plano }: props) {
  const [expanded, setExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState<number | null>(null);
  const [openLogs, setOpenLogs] = useState<boolean>(false);
  const [logs, setLogs] = useState<logPlanoType[]>([
    {
      acao: '',
      user: '',
      data: 0,
      foto: '',
    }
  ]);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState<planoDeAcaoType | null>(null);

  function handleOpenLogs() {
    setOpenLogs(!openLogs);
  }

  function handleOpenEdita() {
    setOpenEdit(!openEdit);
  }

  const { t } = useTranslation('translation');

  const getTagExpirationLabel = (expiration: number) => {
    if (Date.now() > expiration) {
      const now = moment(new Date());
      const limit = moment(new Date(expiration));
      const diff = Number(moment(now.diff(limit, 'milliseconds')).format('D'));
      if (diff === 0) {
        return 'is-light';
      } else if (diff > 0 && diff < 5) {
        return 'is-warning';
      } else if (diff >= 5) {
        return 'is-danger';
      } else {
        return 'is-success';
      }
    } else {
      const difference = expiration - Date.now();
      const resto = difference % 86400000;
      const diff = Number(moment(difference - resto).format('D'));
      if (diff <= 2 || difference < 86400000) {
        return 'is-warning';
      } else {
        return 'is-info';
      }
    }
  };

  const getTaskStatus = (value: string) => {
    switch (value) {
      case 'pendente':
        return (
          <span style={{ width: '100%', display: 'flex' }} className="tag is-warning"><b>{t('ActionPlan.PlanosDeAcaoComp.pending')}</b></span>
        );
      case 'emExecucao':
        return (
          <span style={{ width: '100%', display: 'flex' }} className="tag is-info"><b>{t('ActionPlan.PlanosDeAcaoComp.inExecution')}</b></span>
        );
      case 'concluido':
        return (
          <span style={{ width: '100%', display: 'flex' }} className="tag is-success is-light"><b>{t('ActionPlan.PlanosDeAcaoComp.completed')}</b></span>
        );
      case 'aprovado':
        return (
          <span style={{ width: '100%', display: 'flex' }} className="tag is-success"><b>{t('ActionPlan.PlanosDeAcaoComp.approved')}</b></span>
        );
      case 'rejeitado':
        return (
          <span style={{ width: '100%', display: 'flex' }} className="tag is-danger"><b>{t('ActionPlan.PlanosDeAcaoComp.rejected')}</b></span>
        );
      default: return <></>;
    }
  };

  const calculateTime = (limit: number) => {
    if (Date.now() > limit) {
      const a = moment(new Date());
      const b = moment(new Date(limit));
      const dias = Number(moment(a.diff(b, 'milliseconds')).format('D'));
      return `${t('ActionPlan.PlanosDeAcaoComp.ago')} ${dias} ${dias <= 1 ? t('ActionPlan.PlanosDeAcaoComp.day') : t('ActionPlan.PlanosDeAcaoComp.days')}`;
    } else {
      const test = moment(limit).endOf('day').fromNow();
      return test;
    }
  };

  return (
    <>
      {openEdit && (
        <ModalEditPlan
          handleOpen={handleOpenEdita}
          openModal={openEdit}
          plano={selectedPlano}
          setPlano={setSelectedPlano}
          restarting={false}
        />)}
      {openLogs && <ModalLogs openLogs={openLogs} handleOpenLogs={handleOpenLogs} logs={logs} />}
      <Accordion key={plano.id} expanded={expanded} className="accordeon" sx={{width:'100%'}}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon
            onClick={() => {
              trackEventMatomo('modal detalhe plano de ação', 'click', 'acordeon', 'abre/fecha');
              setExpanded(!expanded);
            }}
          />}>
          <Stack direction='row' width='100%' alignItems='flex-start' justifyContent='space-between'>
            <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={1}>
              <Typography fontWeight={600} variant='h4' sx={{ color: '#67c0ac' }}>
                {plano.nome}
              </Typography>
              {plano.dataEdicao && (
                <Typography color='textDisabled' fontSize='small'>
                  {`${t('ActionPlan.PlanosDeAcaoComp.edited')} - ${new Date(plano.dataEdicao).toLocaleString('pt-BR')}`}
                </Typography>
              )}
              {plano.dataReinicio && (
                <Typography color='textDisabled' fontSize='small'>
                  {`${t('ActionPlan.PlanosDeAcaoComp.reopened')} - ${new Date(plano.dataReinicio).toLocaleString('pt-BR')}`}
                </Typography>
              )}
              <Typography color='textDisabled' fontSize='small'>
                {t('ActionPlan.PlanosDeAcaoComp.createdBy')}&nbsp;
                <b>{plano.criadorNome ? plano.criadorNome : 'Nome não encontrado'}</b>&nbsp;
                {t('ActionPlan.PlanosDeAcaoComp.on')}&nbsp;
                {new Date(Number(plano?.dataCriacao))?.toLocaleString('pt-BR')}&nbsp;
                {t('ActionPlan.PlanosDeAcaoComp.fromChecklist')}
                <b> {plano.formularioNome}</b>
              </Typography>
              <Typography color='textDisabled' fontSize='small'>
                {t('ActionPlan.PlanosDeAcaoComp.planResponsible')}&nbsp;
                <b>{plano.responsavelNome ? plano.responsavelNome : plano.responsavel}</b>
              </Typography>
              <Typography color='textDisabled' fontSize='small'>
                {t('ActionPlan.PlanosDeAcaoComp.planDeadline')}&nbsp;
                <b>{new Date(plano.dataLimite).toLocaleDateString('pt-BR')}</b>
              </Typography>
            </Stack>
            {plano.status === 'aberto' && (
              <Button
                endIcon={<BorderColorIcon />}
                onClick={() => { handleOpenEdita(); setSelectedPlano(plano); }}
                variant='contained'>
                Editar Plano
              </Button>
            )}
          </Stack>
        </AccordionSummary>
        <Divider variant='fullWidth' />
        <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={1} padding={2}>
          <Typography fontWeight={600} color='textDisabled'>
            {t('ActionPlan.PlanosDeAcaoComp.nonConformitiesOfThisPlan')}
          </Typography>
          <Stack>
            {plano.naoConformidades.map((naoConf, index) => (
              <Stack key={index}>
                <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={1} paddingLeft={1}>
                  <Stack direction='row' width='100%' spacing={1.5} alignItems='center' justifyContent='flex-start'>
                    <IconButton
                      onClick={() => {
                        trackEventMatomo('modal detalhe plano de ação', 'click', 'button', 'mostra detalhes subitem');
                        setShowDetails((prev) => prev !== null && prev === index ? null : index);
                      }}
                    >
                      {showDetails !== null && showDetails === index ? <FaEyeSlash /> : <FaEye />}
                    </IconButton>
                    <Typography>
                      {naoConf.nome}
                    </Typography>
                  </Stack>
                </Stack>
                {showDetails !== null && showDetails === index && (
                  <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={1} paddingLeft={4}>
                    <Typography>{t('ActionPlan.PlanosDeAcaoComp.evaluation')}</Typography>
                    {naoConf.avaliacao && naoConf.avaliacao.map((ava, index) => (
                      <Typography key={index} fontWeight={`${naoConf.nota === ava.valor ? 'bold' : '100'}`}>{ava.nome}</Typography>
                    ))}
                    {naoConf.ocorrencia && <Typography color='textDisabled' fontWeight={600} fontSize='small'>{`${t('ActionPlan.PlanosDeAcaoComp.comment')} ${naoConf.ocorrencia}`}</Typography>}
                    {naoConf.img && <ImagesCmp images={naoConf.img} />}
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
          <Typography fontWeight={600}>
            {t('ActionPlan.PlanosDeAcaoComp.tasks')}
          </Typography>
          <Divider variant='fullWidth' flexItem />
          {plano.tasks?.map((task, index) =>
            <Stack key={index} width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={2} direction='column'>
              <Stack direction='row' justifyContent='space-between' alignItems='flex-start' width='100%' key={index}>
                <Stack direction='column' width='100%' spacing={1} justifyContent='flex-start' alignItems='flex-start'>
                  <Stack direction='row' justifyContent='flex-start' alignItems='center' width='100%' spacing={2}>
                    <Tooltip title={task.log ? t('ActionPlan.PlanosDeAcaoComp.viewLogs') : t('ActionPlan.PlanosDeAcaoComp.noLogsAvailable')}>
                      <IconButton
                        onClick={() => {
                          trackEventMatomo('modal detalhe plano de ação', 'click', 'button', 'mostra detalhes logs');
                          setLogs([...task.log as logPlanoType[]]);
                          handleOpenLogs();
                        }}
                        disabled={!task.log}
                        className="button is-small is-white buttonEye">
                        <FaEye />
                      </IconButton>
                    </Tooltip>
                    <Typography fontWeight={600} color='textDisabled'>{task.nome}</Typography>
                  </Stack>
                  <Typography color='textDisabled'>
                    {t('ActionPlan.PlanosDeAcaoComp.responsible')}: <b>{task.responsavelNome ? task.responsavelNome : task.responsavel}</b>
                  </Typography>
                  {task.detalhes && <Typography>{t('ActionPlan.PlanosDeAcaoComp.details')}: {task.detalhes}</Typography>}
                </Stack>
                <Stack direction='column' spacing={1} alignItems='flex-end'>
                  <Stack direction='row' width='100%' alignItems='center' spacing={1}>
                    <Typography component='span' color='textDisabled' fontSize={13}>
                      {t('ActionPlan.PlanosDeAcaoComp.status')}
                    </Typography>
                    {getTaskStatus(task.status)}
                  </Stack>
                  <Stack direction='row' width='100%' alignItems='center' spacing={1}>
                    <Typography variant='subtitle1' color='textDisabled' fontSize={13}>
                      {t('Reports.ticketList.deadline')}
                    </Typography>
                    <span
                      style={{ width: '100%', display: 'flex' }}
                      className={`tag ${getTagExpirationLabel(task.dataLimite)}`}>
                      {new Date(task.dataLimite).toLocaleDateString('pt-BR')}
                    </span>
                  </Stack>
                  {Date.now() > task.dataLimite && (
                    <span className={`tag ${getTagExpirationLabel(task.dataLimite)}`}>
                      {t('ActionPlan.PlanosDeAcaoComp.expiredTask')}&nbsp;{calculateTime(task.dataLimite)}
                    </span>
                  )}
                  {Date.now() <= task.dataLimite && (
                    <span className={`tag ${getTagExpirationLabel(task.dataLimite)}`}>
                      {t('ActionPlan.PlanosDeAcaoComp.taskExpires')}&nbsp;{calculateTime(task.dataLimite)}
                    </span>
                  )}
                </Stack>
              </Stack>
              <Divider variant='middle' flexItem />
            </Stack>
          )}
        </Stack>
      </Accordion>
    </>
  );
}
